import {
  ContentType,
  MessageExtractKind,
  MessageExtractOutcome,
  MessageExtractStatus,
  ProcessRunStatus,
  CollectionKind,
  TemplateLayout,
  TemplateStatus,
  CaptureFlow,
} from './enums';
import type { FileProviderValue } from './drives';
import type { Prisma, SearchSegmentKind } from '@prisma/client';
import { z } from 'zod';
import type { ContentListProjection } from './filter';
import type { ParsedTemplateEntry, TemplateEntryDef, TemplateField } from './template-fields';
import { mutableContentTypeSchema } from './content-entities';
import type { ContentExternalRefSummary, ObligationRollup } from './content-entities';
import type {
  ContentBase,
  ContentPartySummary,
  MessageExtractHighlight,
  Anchor,
  Event,
  Message,
  TransmittalPartySummary,
} from './content-entities';

export interface Photo extends ContentBase {
  contentType: 'PHOTO';
  uri: string;
  parentAnchorId: number;
  parentAnchorTitle: string;
  photoCount: 1;
  recordingCount: 0;
}

export interface Recording extends ContentBase {
  contentType: 'RECORDING';
  uri: string;
  parentAnchorId: number;
  parentAnchorTitle: string;
  transcript?: string;
  length: number;
  photoCount: 0;
  recordingCount: 1;
}

export interface Video extends ContentBase {
  contentType: 'VIDEO';
  uri: string;
  mimeType: string;
  size: number;
  durationSeconds: number | null;
  parentAnchorId: number;
  parentAnchorTitle: string;
  photoCount: 0;
  recordingCount: 0;
  videoCount: 1;
}

export interface FileContent extends ContentBase {
  contentType: 'FILE';
  uri: string | null;
  provider: FileProviderValue;
  driveId: number | null;
  providerItemId: string | null;
  providerPath: string | null;
  webUrl: string | null;
  mimeType?: string;
  size?: number;
  parentAnchorId: number;
  parentAnchorTitle: string;
  photoCount: 0;
  recordingCount: 0;
}

export interface UrlContent extends ContentBase {
  contentType: 'URL';
  url: string;
  parentAnchorId: number;
  parentAnchorTitle: string;
  photoCount: 0;
  recordingCount: 0;
}

export interface DocumentSummary {
  id: number;
  title: string;
}

export interface DocumentChildSummary extends DocumentSummary {
  sortOrder: number;
}

export interface DocumentRootSummary extends DocumentSummary {
  depth: number;
  path: string | null;
}

export interface DocumentHierarchyTreeNode extends DocumentSummary {
  outlineNumber: string;
  depth: number;
  sortOrder: number;
  path: string | null;
  children: DocumentHierarchyTreeNode[];
}

export interface Document extends ContentBase {
  contentType: 'DOCUMENT';
  parentCode: string | null;
  rootCode: string | null;
  sortOrder: number;
  depth: number;
  path: string | null;
  parentDocument: DocumentSummary | null;
  childDocuments: DocumentChildSummary[];
  rootDocument: DocumentRootSummary | null;
  hierarchyTree?: DocumentHierarchyTreeNode;
  /** Union of this root's own tags and every tag on its sections. Document ROOTS in list results only. */
  descendantTagsIds?: number[];
}

export interface NeoChatContent extends ContentBase {
  contentType: 'NEO_CHAT';
  latestSessionState?: Record<string, unknown>;
  latestActiveTool?: Record<string, unknown>;
  latestPendingTool?: Record<string, unknown>;
  latestSuggestedActions?: Record<string, unknown>;
  lastMessageSequence: number;
}

export interface ProcessRunContent extends ContentBase {
  contentType: 'PROCESS_RUN';
  processId: string | null;
  processNameSnapshot: string;
  agentKeySnapshot: string | null;
  status: 'QUEUED' | 'RUNNING' | 'WAITING_APPROVAL' | 'SUCCEEDED' | 'FAILED' | 'SKIPPED' | 'DEAD_LETTER' | 'CANCELLED';
  /** Denormalized from last ProvenanceEntry sequence (automation milestone index). */
  lastEntrySequence: number;
  /** Latest execution snapshot; immutable history remains in ProvenanceEntry rows. */
  latestRuntimeState?: Record<string, unknown>;
}

export interface ManualSessionContent extends ContentBase {
  contentType: 'MANUAL_SESSION';
}

export interface CollectionContent extends ContentBase {
  contentType: 'COLLECTION';
  kind: CollectionKind;
}

export interface TemplateContent extends ContentBase {
  contentType: 'TEMPLATE';
  whenToUse: string;
  layout: TemplateLayout;
  status: TemplateStatus;
  captureFlow: CaptureFlow;
  publishWhenComplete: boolean;
  definitionCode: string | null;
  sourceFileCode: string | null;
  exclusions: { ids: number[]; reason: string }[];
  publishedAt: string | null;
  retiredAt: string | null;
}

export interface MessageExtractContent extends ContentBase {
  contentType: 'MESSAGE_EXTRACT';
  kind: MessageExtractKind;
  status: MessageExtractStatus;
  outcome: MessageExtractOutcome | null;
  dueAt: string | null;
  burstId: number | null;
  assignee: ContentPartySummary | null;
  sourceHighlight: MessageExtractHighlight | null;
}

export interface MyObligationPriorityItem {
  extractContentId: number;
  /** Caller's latest MEMBER_PRIORITY_SET value (1–10, 1 = most important); null = not set/cleared. */
  personalPriority: number | null;
  /** Shared system prediction (LOWER = more important); payload-only — never displayed raw to members. */
  computedScore: number | null;
}

export const MESSAGE_EXTRACT_OUTCOMES_BY_KIND: Record<
  MessageExtractKind,
  readonly MessageExtractOutcome[]
> = {
  DECISION: [MessageExtractOutcome.AFFIRMED, MessageExtractOutcome.DECLINED],
  QUESTION: [MessageExtractOutcome.AFFIRMED, MessageExtractOutcome.DECLINED],
  ACTION: [],
  FAULT: [],
  RISK: [MessageExtractOutcome.MITIGATED, MessageExtractOutcome.ACCEPTED],
};

export type Content =
  | Anchor
  | Event
  | Message
  | Photo
  | Recording
  | Video
  | Document
  | FileContent
  | UrlContent
  | NeoChatContent
  | ProcessRunContent
  | ManualSessionContent
  | CollectionContent
  | TemplateContent
  | MessageExtractContent;

export interface DocumentDetail extends Omit<Document, 'hierarchyTree'> {
  hierarchyTree: DocumentHierarchyTreeNode;
}

export interface DocumentHierarchyMutationResult {
  document: DocumentDetail;
}

export interface DocumentDeleteSubtreeResult {
  warning: {
    deletedRootId: number;
    deletedCount: number;
    deletedTitle?: string;
  };
}

export interface DocumentRenderCitation {
  id: number;
  title: string;
  url: string;
}

export type DocumentRenderAttachmentKind =
  | 'PHOTO'
  | 'RECORDING'
  | 'VIDEO'
  | 'FILE'
  | 'EVENT'
  | 'ANCHOR'
  | 'DOCUMENT'
  | 'MESSAGE'
  | 'NEO_CHAT'
  | 'PROCESS_RUN'
  | 'MANUAL_SESSION';

export interface DocumentRenderAttachmentSummary {
  id: number;
  code: string;
  contentType: DocumentRenderAttachmentKind;
  title: string;
  obligationRollup: ObligationRollup | null;
  externalRefs: ContentExternalRefSummary[];
}

export interface DocumentRenderSectionTag {
  id: number;
  name: string;
  tagGroupId: number | null;
}

/** Derived from the rendered body: false while the token is still present or the label/value cell is empty or a token; column fields are always true (rows are added, not filled). */
export type DocumentRenderTemplateField = TemplateField & { filled: boolean };

export interface DocumentRenderTemplateSection {
  guidance: string;
  blockIds: number[];
  tables: { blockId: number; mode: 'repeat' | 'fixed'; photoColumn: string | null; bands: boolean }[];
  /** Bracketed tokens in the body, [photo] excluded. */
  placeholderCount: number;
  fields: DocumentRenderTemplateField[];
  entry: TemplateEntryDef | null;
  /** Parsed from the instance body per the entry-block dialect; null on a definition, whose preview is never data. */
  entries: ParsedTemplateEntry[] | null;
  /** True while the title still equals the default the section was created with. */
  untitled: boolean;
}

export interface DocumentRenderSection {
  id: number;
  code: string;
  parentCode: string | null;
  depth: number;
  sortOrder: number;
  outlineNumber: string;
  title: string;
  description: string;
  tags: DocumentRenderSectionTag[];
  citations: DocumentRenderCitation[];
  otherAttachments: DocumentRenderAttachmentSummary[];
  /** Present on definition-root sections only. */
  templateSection: DocumentRenderTemplateSection | null;
}

export interface DocumentRenderMaintainer {
  processId: string;
  name: string;
  triggerInWords: string;
  lastRun: {
    status: ProcessRunStatus;
    summaryLine: string | null;
    finishedAt: string | null;
  } | null;
}

export interface DocumentRenderTemplateWriteUp {
  written: boolean;
  /** Required member-or-either fields whose token or label/value cell is still unfilled; column fields never count. */
  unresolvedRequiredFields: number;
}

export interface DocumentRenderTemplate {
  role: 'definition' | 'instance';
  templateContentId: number;
  templateCode: string;
  name: string;
  status: TemplateStatus;
  whenToUse: string;
  layout: TemplateLayout;
  captureFlow: CaptureFlow;
  draft?: {
    outlineBacked: boolean;
    sourceFileCode: string | null;
    uncoveredBlocks: { id: number; kind: string; preview: string }[];
    excluded: { ids: number[]; reason: string }[];
  };
  writeUp?: DocumentRenderTemplateWriteUp;
}

export interface DocumentRender {
  rootContentId: number;
  rootCode: string;
  rootTitle: string;
  sections: DocumentRenderSection[];
  maintainer: DocumentRenderMaintainer | null;
  template: DocumentRenderTemplate | null;
}

export type ContentDetail =
  | Anchor
  | Event
  | Message
  | Photo
  | Recording
  | Video
  | DocumentDetail
  | FileContent
  | UrlContent
  | NeoChatContent
  | ProcessRunContent
  | ManualSessionContent
  | CollectionContent
  | TemplateContent
  | MessageExtractContent;

export type ActiveContentType = z.infer<typeof mutableContentTypeSchema>;

// Type mapping for content type to return type
export type ContentTypeMap = {
  [ContentType.ANCHOR]: Anchor;
  [ContentType.EVENT]: Event;
  [ContentType.MESSAGE]: Message;
  [ContentType.PHOTO]: Photo;
  [ContentType.RECORDING]: Recording;
  [ContentType.VIDEO]: Video;
  [ContentType.DOCUMENT]: Document;
  [ContentType.FILE]: FileContent;
  [ContentType.URL]: UrlContent;
  [ContentType.NEO_CHAT]: NeoChatContent;
  [ContentType.PROCESS_RUN]: ProcessRunContent;
  [ContentType.MANUAL_SESSION]: ManualSessionContent;
  [ContentType.MESSAGE_EXTRACT]: MessageExtractContent;
  [ContentType.TEMPLATE]: TemplateContent;
};

export type ContentDetailTypeMap = {
  [ContentType.ANCHOR]: Anchor;
  [ContentType.EVENT]: Event;
  [ContentType.MESSAGE]: Message;
  [ContentType.PHOTO]: Photo;
  [ContentType.RECORDING]: Recording;
  [ContentType.VIDEO]: Video;
  [ContentType.DOCUMENT]: DocumentDetail;
  [ContentType.FILE]: FileContent;
  [ContentType.URL]: UrlContent;
  [ContentType.NEO_CHAT]: NeoChatContent;
  [ContentType.PROCESS_RUN]: ProcessRunContent;
  [ContentType.MANUAL_SESSION]: ManualSessionContent;
  [ContentType.MESSAGE_EXTRACT]: MessageExtractContent;
  [ContentType.TEMPLATE]: TemplateContent;
};

/**
 * Content list response type
 */
export interface SearchMatch {
  segmentKind: SearchSegmentKind;
  locator: Prisma.JsonValue | null;
  snippet: string | null;
}

export interface ContentListResult {
  projection: ContentListProjection;
  items: ContentListItem[];
  nextCursor: number | null;
  totalItems: number;
  matches?: Record<number, SearchMatch>;
}

export interface ContentConversationListItem {
  projectionType: 'CONVERSATION';
  id: number;
  representativeMessageId: number;
  representativeMessage: Message;
  latestMessageAt: string;
  messageCount: number;
  parties: TransmittalPartySummary[];
  memberHasContributed: boolean;
  latestSnippet: string | null;
  hasUnread: boolean;
  hasAttachments: boolean;
  rootCode: string | null;
  providerThreadId: string | null;
  memberExternalAccountId: number | null;
  externalEmail: string | null;
}

export type ContentListItem = Content | ContentConversationListItem;

/**
 * Content action result type
 */
export interface ContentActionResult {
  success: boolean;
  message?: string;
  // Action payload shape varies by content type and action.
  [key: string]: any;
}
